import React from 'react';
import { SideSheet, SideSheetCloseButton } from './SideSheet';
import { StatusBadge, SourceBadge } from './Badges';
import { Pencil, Save, X, Plus } from 'lucide-react';
import { ORDER_TRACKING, badgeStyle } from '../../lib/statusConfig';
import { ROLES } from '../../lib/routeConfig';
import { ordersApi } from '../../lib/ordersApi';
import { formatClientName } from '../../lib/formatters';
import { toastSuccess, toastError } from '../../lib/toast';
import ProductThumb from './ProductThumb';
import { ProductItemBlock, MAX_ITEMS } from './NewWhatsappOrderDialog';

// Extract a user-facing French message from an API/network error.
// - Backend errors surface as `{ detail: "…" }` (Pydantic) or plain string.
// - Network / 5xx errors are already toasted by `lib/api.js` — we return
//   `null` here so callers can skip the duplicate toast.
function errorMsg(err, fallback) {
    if (!err) return fallback;
    if (err.network) return null;                    // already toasted by api.js
    if (typeof err.detail === 'string') return err.detail;
    if (typeof err.message === 'string' && err.message.length > 0) return err.message;
    return fallback;
}

// Helper: emit an error toast only when we actually have a message
// (avoids duplicating the network toast surfaced by api.js).
function reportError(err, fallback) {
    const msg = errorMsg(err, fallback);
    if (msg) toastError(msg);
}

// -----------------------------------------------------------------------------
// Order detail sheet — non-modal, slides in from the right, 420px wide.
// Sections: client, order, delivery, source+assignment, timeline.
// Each editable section has its own "Éditer/Sauvegarder" workflow so a partial
// save doesn't affect the other sections.
// -----------------------------------------------------------------------------
export default function OrderDetailSheet({ open, onOpenChange, orderId, user, callCenters, shippingCompanies, productCatalog = [], onOrderChanged }) {
    const [state, setState] = React.useState({ status: 'idle', data: null, error: null });

    // Race-safe loader (Phase 12.3 fix). Every call is stamped with a
    // monotonic req_id; a completed request is applied ONLY if:
    //   1. the component is still mounted (mountedRef), and
    //   2. its req_id is the most recent one issued (reqRef).
    // This defuses two races that previously could pin the sheet on a stale
    // "Commande introuvable" error state:
    //   a) React StrictMode dev double-mount: two overlapping load() calls,
    //      whichever resolves LAST would win regardless of orderId.
    //   b) Quickly switching orderId (e.g. opening rappel A, then B): the
    //      A request could resolve after B and clobber B's fresh data.
    const mountedRef = React.useRef(true);
    const reqRef = React.useRef(0);
    React.useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const load = React.useCallback(async (id) => {
        if (!id) return;
        const reqId = ++reqRef.current;
        setState({ status: 'loading', data: null, error: null });
        try {
            const data = await ordersApi.get(id);
            if (!mountedRef.current || reqRef.current !== reqId) return; // stale
            setState({ status: 'ready', data, error: null });
        } catch (e) {
            if (!mountedRef.current || reqRef.current !== reqId) return; // stale
            setState({ status: 'error', data: null, error: e.detail || e.message });
        }
    }, []);

    // Trigger the load whenever the sheet becomes open AND we have a valid
    // orderId. Also re-load if orderId itself changes while open (switching
    // from one order to another without closing). If open=true but orderId
    // is still null, we stay in 'idle' — the sheet just shows nothing (no
    // false "Commande introuvable" flash).
    React.useEffect(() => {
        if (open && orderId) {
            load(orderId);
        } else if (!open) {
            // Reset when the sheet closes so the next open starts fresh.
            reqRef.current++;
            setState({ status: 'idle', data: null, error: null });
        }
    }, [open, orderId, load]);

    const applyChange = (fresh) => {
        setState({ status: 'ready', data: fresh, error: null });
        onOrderChanged?.(fresh.order);
    };

    const canReassign = user?.role === ROLES.SUPER_ADMIN;

    return (
        <SideSheet open={open} onOpenChange={onOpenChange} width={420} testId="order-detail-sheet">
            {state.status === 'loading' && (
                <div className="flex-1 flex items-center justify-center text-[#6B7280] text-sm">Chargement…</div>
            )}
            {state.status === 'error' && (
                <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6">
                    <div data-testid="detail-error" className="text-sm text-[#EF4444]">{typeof state.error === 'string' ? state.error : 'Erreur'}</div>
                    <SideSheetCloseButton onClose={() => onOpenChange(false)} />
                </div>
            )}
            {state.status === 'ready' && (
                <DetailBody
                    data={state.data}
                    user={user}
                    callCenters={callCenters}
                    shippingCompanies={shippingCompanies}
                    productCatalog={productCatalog}
                    canReassign={canReassign}
                    onClose={() => onOpenChange(false)}
                    onPatch={async (patch) => {
                        const fresh = await ordersApi.patch(orderId, patch);
                        applyChange(fresh);
                    }}
                    onAssign={async (cc_id) => {
                        const res = await ordersApi.assign(orderId, cc_id);
                        // Refresh full detail (assignment may unlock/lock fields)
                        const fresh = await ordersApi.get(orderId);
                        applyChange(fresh);
                    }}
                />
            )}
        </SideSheet>
    );
}


function DetailBody({ data, user, callCenters, shippingCompanies, productCatalog, canReassign, onClose, onPatch, onAssign }) {
    const { order, status_history } = data;
    const currentStatus = order.order_tracking;
    return (
        <>
            <header className="h-14 shrink-0 px-4 flex items-center justify-between border-b border-[#E5E7EB]">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="text-sm font-semibold text-[#1A1D2E] truncate" data-testid="detail-short-id">#{order.short_id}</div>
                    <StatusBadge status={currentStatus} testId="detail-status-badge" />
                </div>
                <SideSheetCloseButton onClose={onClose} />
            </header>

            <div className="flex-1 overflow-y-auto p-4 space-y-6">
                <ClientSection order={order} onPatch={onPatch} />
                <OrderSection order={order} productCatalog={productCatalog} onPatch={onPatch} />
                <DeliverySection
                    order={order}
                    onPatch={onPatch}
                    shippingCompanies={shippingCompanies}
                />
                <SourceSection
                    order={order}
                    canReassign={canReassign}
                    callCenters={callCenters}
                    onAssign={onAssign}
                />
                <HistoryTimeline history={status_history} />
            </div>
        </>
    );
}


function SectionShell({ title, testId, editing, onToggleEdit, onSave, saving, disabled = false, children }) {
    return (
        <section data-testid={testId} className="space-y-2">
            <div className="flex items-center justify-between">
                <h3 className="text-[13px] font-semibold uppercase tracking-wider text-[#6B7280]">{title}</h3>
                {!disabled && (
                    <div className="flex items-center gap-1">
                        {editing ? (
                            <>
                                <button
                                    type="button"
                                    data-testid={`${testId}-save`}
                                    disabled={saving}
                                    onClick={onSave}
                                    className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs font-medium text-[#4B3FD8] bg-[#4B3FD8]/10 hover:bg-[#4B3FD8]/20 disabled:opacity-60"
                                >
                                    <Save size={12} strokeWidth={2} />
                                    {saving ? '…' : 'Sauvegarder'}
                                </button>
                                <button
                                    type="button"
                                    onClick={onToggleEdit}
                                    className="inline-flex items-center h-7 px-2 rounded-md text-xs text-[#6B7280] hover:bg-[#F5F6FA]"
                                >
                                    <X size={12} strokeWidth={2} />
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                data-testid={`${testId}-edit`}
                                onClick={onToggleEdit}
                                className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs text-[#6B7280] hover:bg-[#F5F6FA]"
                            >
                                <Pencil size={12} strokeWidth={2} />
                                Éditer
                            </button>
                        )}
                    </div>
                )}
            </div>
            <div className="rounded-lg border border-[#E5E7EB] bg-white p-3 space-y-2 text-sm">
                {children}
            </div>
        </section>
    );
}

function Row({ label, children }) {
    return (
        <div className="flex items-start justify-between gap-4">
            <div className="text-xs font-medium text-[#6B7280] pt-1 shrink-0 w-32">{label}</div>
            <div className="text-[#1A1D2E] text-right flex-1 min-w-0">{children}</div>
        </div>
    );
}

const inputCls = 'w-full h-8 px-2 rounded-md border border-[#E5E7EB] text-sm text-[#1A1D2E] focus:outline-none focus:border-[#4B3FD8]';

function ClientSection({ order, onPatch }) {
    const [editing, setEditing] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    // Phase 11.9: `region` no longer rendered/edited from the sheet. The
    // field stays in the DB (mapped from Shopify webhook, patchable via
    // API), but it is intentionally hidden from all client-facing UI.
    const [f, setF] = React.useState({ full_name: order.full_name, phone: order.phone, city: order.city, address: order.address || '' });
    React.useEffect(() => { if (!editing) setF({ full_name: order.full_name, phone: order.phone, city: order.city, address: order.address || '' }); }, [order, editing]);
    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...f, address: f.address && f.address.trim() ? f.address.trim() : null };
            await onPatch(payload);
            setEditing(false);
            toastSuccess('Informations client mises à jour.');
        } catch (e) {
            reportError(e, 'Erreur lors de la sauvegarde des informations client.');
        } finally {
            setSaving(false);
        }
    };
    return (
        <SectionShell title="Informations client" testId="section-client" editing={editing} saving={saving} onToggleEdit={() => setEditing((v) => !v)} onSave={save}>
            <Row label="Nom complet">{editing ? <input data-testid="edit-full-name" className={inputCls} value={f.full_name || ''} onChange={(e) => setF({ ...f, full_name: e.target.value })} /> : (formatClientName(order.full_name) || '—')}</Row>
            <Row label="Téléphone">{editing ? <input data-testid="edit-phone" className={inputCls} value={f.phone || ''} onChange={(e) => setF({ ...f, phone: e.target.value })} /> : (order.phone || '—')}</Row>
            <Row label="Ville">{editing ? <input data-testid="edit-city" className={inputCls} value={f.city || ''} onChange={(e) => setF({ ...f, city: e.target.value })} /> : (order.city || '—')}</Row>
            <Row label="Adresse">{editing ? <input data-testid="edit-address" className={inputCls} value={f.address || ''} onChange={(e) => setF({ ...f, address: e.target.value })} /> : <span data-testid="detail-address">{order.address || '—'}</span>}</Row>
        </SectionShell>
    );
}

function OrderSection({ order, productCatalog, onPatch }) {
    // Phase 16.1 — single unified edit flow for both single-item (flat) and
    // multi-item (`line_items`) orders. Parent owns the `editing` state so
    // the read-only branches (single / multi) can share the exact same
    // pencil button and delegate to `OrderSectionEdit` when toggled on.
    const [editing, setEditing] = React.useState(false);
    if (editing) {
        return (
            <OrderSectionEdit
                order={order}
                productCatalog={productCatalog}
                onCancel={() => setEditing(false)}
                onSaved={() => setEditing(false)}
                onPatch={onPatch}
            />
        );
    }
    const isMulti = Array.isArray(order.line_items) && order.line_items.length > 0;
    if (isMulti) return <OrderSectionMultiView order={order} onEdit={() => setEditing(true)} />;
    return <OrderSectionSingleView order={order} onEdit={() => setEditing(true)} />;
}

function OrderSectionMultiView({ order, onEdit }) {
    const items = order.line_items || [];
    const totalQty = items.reduce((s, it) => s + (Number(it.quantity) || 0), 0);
    return (
        <SectionShell title="Commande" testId="section-order" editing={false} onToggleEdit={onEdit}>
            <ul data-testid="detail-line-items" className="space-y-3">
                {items.map((it, i) => (
                    <li
                        key={i}
                        data-testid={`detail-line-item-${i}`}
                        className={`flex items-start gap-3 ${i > 0 ? 'pt-3 border-t border-[#F3F4F6]' : ''}`}
                    >
                        <ProductThumb src={it.product_image_url} alt={it.product_name} size="sm" />
                        <div className="flex-1 min-w-0 space-y-0.5">
                            <div className="text-sm font-medium text-[#1A1D2E] break-normal">
                                {it.product_name || '—'}
                            </div>
                            {it.product_variant && (
                                <div className="text-xs text-[#6B7280] break-normal">{it.product_variant}</div>
                            )}
                            <div className="text-xs text-[#6B7280]">Qté&nbsp;: <span className="tabular-nums text-[#1A1D2E]">{it.quantity}</span></div>
                        </div>
                    </li>
                ))}
            </ul>
            <div className="pt-3 mt-1 border-t border-[#E5E7EB] space-y-1">
                <Row label="Nombre de produits">
                    <span data-testid="detail-line-items-count" className="tabular-nums">{items.length}</span>
                </Row>
                <Row label="Quantité totale">
                    <span data-testid="detail-total-quantity" className="tabular-nums">{totalQty}</span>
                </Row>
                <Row label="Prix total">
                    <span data-testid="detail-total-price" className="font-semibold">{Number(order.price || 0).toFixed(2)} MAD</span>
                </Row>
            </div>
        </SectionShell>
    );
}

function OrderSectionSingleView({ order, onEdit }) {
    const total = (Number(order.quantity) || 0) * (Number(order.price) || 0);
    return (
        <SectionShell title="Commande" testId="section-order" editing={false} onToggleEdit={onEdit}>
            <div className="flex items-start gap-4">
                <ProductThumb src={order.product_image_url} alt={order.product_name} size="lg" />
                {/* Info column. The two long fields (Produit + Variante) use a
                    stacked "label above value" layout so the value can span
                    the full column width — with a 96 px thumbnail eating 40 %
                    of the 420 px sheet, a shared 128 px label + right-aligned
                    value would only leave ~105 px for the text and force
                    long product names like "Tableau Photo Unique Personnalisé"
                    to wrap word-by-word (Phase 14.3.1 fix). The short numeric
                    rows (Quantité, Prix) keep the shared Row layout for
                    consistency. */}
                <div className="flex-1 min-w-0 space-y-2 text-sm">
                    <div>
                        <div className="text-xs font-medium text-[#6B7280]">Produit</div>
                        <div className="text-[#1A1D2E] mt-1 break-normal">
                            {order.product_name || '—'}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs font-medium text-[#6B7280]">Variante</div>
                        <div data-testid="detail-product-variant" className="text-[#6B7280] mt-1 break-normal">
                            {order.product_variant || '—'}
                        </div>
                    </div>
                    <div className="pt-1 space-y-1">
                        <Row label="Quantité">{order.quantity}</Row>
                        <Row label="Prix unitaire">{`${order.price} MAD`}</Row>
                        <Row label="Prix total"><span data-testid="detail-total-price-single" className="font-semibold">{total.toFixed(2)} MAD</span></Row>
                    </div>
                </div>
            </div>
        </SectionShell>
    );
}

// Phase 16.1 — helper: rebuild the modal's `product_options` map from a
// flat variant string. The variant is stored as options joined by " / " in
// catalog order (same encoding as the creation modal). Splitting and
// mapping by index preserves the round-trip. If a chunk doesn't match a
// valid option value for its group, the map slot stays empty — the user
// must complete it before Save (validation blocks otherwise).
function _splitVariantIntoOptions(productVariant, product) {
    const result = {};
    const groups = product?.options || [];
    if (!groups.length) return result;
    const parts = (productVariant || '').split(' / ');
    groups.forEach((g, idx) => {
        const raw = parts[idx];
        if (raw === undefined) return;
        const val = raw.trim();
        if ((g.values || []).includes(val)) {
            result[g.name] = val;
        }
    });
    return result;
}

function _initEditItem(rawItem, productCatalog) {
    const name = rawItem?.product_name || '';
    const matched = productCatalog.find((p) =>
        (p.name || '').trim().toLowerCase() === name.trim().toLowerCase()
    );
    return {
        product_name: name,
        product_options: _splitVariantIntoOptions(rawItem?.product_variant, matched),
        quantity: Number(rawItem?.quantity) || 1,
    };
}

function OrderSectionEdit({ order, productCatalog, onCancel, onSaved, onPatch }) {
    // Initialise items from get_items(order) equivalent: multi → line_items,
    // single → wrap flat fields into a 1-item list.
    const initialItems = React.useMemo(() => {
        const src = Array.isArray(order.line_items) && order.line_items.length > 0
            ? order.line_items
            : [{
                product_name: order.product_name,
                product_variant: order.product_variant,
                quantity: order.quantity,
            }];
        return src.map((it) => _initEditItem(it, productCatalog));
    }, [order, productCatalog]);

    const [items, setItems] = React.useState(initialItems);
    const [price, setPrice] = React.useState(order.price ?? '');
    const [saving, setSaving] = React.useState(false);
    const [err, setErr] = React.useState(null);
    const [focusIdx, setFocusIdx] = React.useState(-1);

    const updateItem = (idx, next) => {
        setItems((prev) => prev.map((it, i) => (i === idx ? next : it)));
    };

    const addItem = () => {
        setItems((prev) => {
            if (prev.length >= MAX_ITEMS) return prev;
            const next = [...prev, { product_name: '', product_options: {}, quantity: 1 }];
            setFocusIdx(next.length - 1);
            return next;
        });
    };

    const removeItem = (idx) => {
        setItems((prev) => {
            if (prev.length <= 1) return prev;
            return prev.filter((_, i) => i !== idx);
        });
        setFocusIdx(-1);
    };

    const itemComplete = (item) => {
        if (!item.product_name) return false;
        const matched = productCatalog.find((p) =>
            (p.name || '').trim().toLowerCase() === (item.product_name || '').trim().toLowerCase()
        );
        const groups = matched?.options || [];
        if (groups.length > 0 && !groups.every((g) => !!item.product_options[g.name])) return false;
        if (!(Number(item.quantity) >= 1)) return false;
        return true;
    };

    const allItemsComplete = items.length > 0 && items.every(itemComplete);
    const priceValid = Number(price) > 0;

    const buildItemPayload = (item) => {
        const matched = productCatalog.find((p) =>
            (p.name || '').trim().toLowerCase() === (item.product_name || '').trim().toLowerCase()
        );
        const groups = matched?.options || [];
        const variant = groups.length > 0
            ? groups.map((g) => item.product_options[g.name]).filter(Boolean).join(' / ')
            : '';
        return {
            product_name: item.product_name.trim(),
            product_variant: variant || null,
            quantity: Number(item.quantity) || 1,
        };
    };

    const save = async () => {
        if (!allItemsComplete) {
            setErr('Veuillez compléter tous les produits (nom, options, quantité).');
            return;
        }
        if (!priceValid) {
            setErr('Le prix total doit être supérieur à 0.');
            return;
        }
        setSaving(true); setErr(null);
        try {
            await onPatch({
                line_items: items.map(buildItemPayload),
                price: Number(price),
            });
            toastSuccess('Commande mise à jour.');
            onSaved();
        } catch (e) {
            reportError(e, 'Erreur lors de la sauvegarde de la commande.');
            setErr(typeof e?.detail === 'string' ? e.detail : (e?.message || 'Erreur'));
        } finally {
            setSaving(false);
        }
    };

    const canAdd = items.length < MAX_ITEMS;
    const canRemove = items.length > 1;

    return (
        <SectionShell
            title="Commande"
            testId="section-order"
            editing={true}
            saving={saving}
            onToggleEdit={onCancel}
            onSave={save}
        >
            <div className="space-y-3">
                {items.map((item, idx) => (
                    <ProductItemBlock
                        key={idx}
                        index={idx}
                        item={item}
                        productCatalog={productCatalog}
                        canRemove={canRemove}
                        onChange={(next) => updateItem(idx, next)}
                        onRemove={() => removeItem(idx)}
                        autoFocus={focusIdx === idx}
                        testIdPrefix="edit-order"
                    />
                ))}
                {canAdd ? (
                    <button
                        type="button"
                        data-testid="edit-order-add-item"
                        onClick={addItem}
                        className="w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-lg border border-dashed border-[#4B3FD8]/40 bg-white text-sm font-medium text-[#4B3FD8] hover:bg-[#4B3FD8]/5"
                    >
                        <Plus size={14} strokeWidth={2} />
                        Ajouter un autre produit
                    </button>
                ) : (
                    <div className="text-[11px] text-[#6B7280] text-center">
                        Maximum {MAX_ITEMS} produits par commande.
                    </div>
                )}
                <div className="pt-3 border-t border-[#E5E7EB]">
                    <label className="block text-xs font-medium text-[#6B7280]">
                        <span className="block mb-1">Prix total <span className="text-[#EF4444]">*</span></span>
                        <div className="relative">
                            <input
                                data-testid="edit-order-price"
                                type="number"
                                step="0.01"
                                min="0"
                                required
                                value={price}
                                onChange={(e) => setPrice(e.target.value)}
                                className="w-full h-9 pr-12 pl-3 text-sm rounded-md border border-[#E5E7EB] bg-white text-[#1A1D2E] focus:outline-none focus:border-[#4B3FD8]"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#6B7280]">MAD</span>
                        </div>
                    </label>
                </div>
                {err && (
                    <div data-testid="edit-order-error" className="text-sm text-[#EF4444]">
                        {err}
                    </div>
                )}
            </div>
            {/* ProductItemBlock uses className="input" on its native <select>
                and quantity <input>. The class is declared inside the
                creation modal's <style> tag which is only mounted when the
                modal is open — replicate the same 4-line rule here so the
                edit UI renders identically inside the sheet. */}
            <style>{`
                .input { height: 36px; padding: 0 12px; border-radius: 8px; border: 1px solid #E5E7EB; background: #fff; font-size: 14px; color: #1A1D2E; width: 100%; }
                .input:focus { outline: none; border-color: #4B3FD8; }
                textarea.input { padding: 8px 12px; }
            `}</style>
        </SectionShell>
    );
}

function DeliverySection({ order, onPatch, shippingCompanies }) {
    const [editing, setEditing] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [f, setF] = React.useState({ shipping_company: order.shipping_company || '' });
    React.useEffect(() => { if (!editing) setF({ shipping_company: order.shipping_company || '' }); }, [order, editing]);
    const save = async () => {
        setSaving(true);
        try {
            // Phase 11.7: the client removed `confirmed_by` and
            // `nombre_de_commandes` from the UI. The backend fields still
            // exist (we don't touch them here), but we no longer expose the
            // edit path — Livraison only manages the shipping company.
            await onPatch({ shipping_company: f.shipping_company || null });
            setEditing(false);
            toastSuccess('Livraison mise à jour.');
        } catch (e) {
            reportError(e, 'Erreur lors de la sauvegarde de la livraison.');
        } finally {
            setSaving(false);
        }
    };
    return (
        <SectionShell title="Livraison" testId="section-delivery" editing={editing} saving={saving} onToggleEdit={() => setEditing((v) => !v)} onSave={save}>
            <Row label="Transporteur">
                {editing ? (
                    <select
                        data-testid="edit-shipping"
                        className={`${inputCls} ${f.shipping_company ? 'uppercase' : ''}`}
                        value={f.shipping_company}
                        onChange={(e) => setF({ ...f, shipping_company: e.target.value })}
                    >
                        <option value="" style={{ textTransform: 'none' }}>—</option>
                        {shippingCompanies.map((sc) => (
                            <option key={sc.id} value={sc.name} className="uppercase">{sc.name}</option>
                        ))}
                    </select>
                ) : (order.shipping_company ? <span className="uppercase">{order.shipping_company}</span> : '—')}
            </Row>
        </SectionShell>
    );
}

function SourceSection({ order, canReassign, callCenters, onAssign }) {
    const [assigning, setAssigning] = React.useState(false);
    const [choice, setChoice] = React.useState(order.assigned_to || '');
    React.useEffect(() => { setChoice(order.assigned_to || ''); }, [order.assigned_to]);
    const doAssign = async () => {
        if (!choice || choice === order.assigned_to) return;
        setAssigning(true);
        try {
            await onAssign(choice);
            toastSuccess('Commande réassignée.');
        } catch (e) {
            reportError(e, 'Erreur lors de la réassignation.');
        } finally {
            setAssigning(false);
        }
    };
    return (
        <SectionShell title="Source & Attribution" testId="section-source" disabled>
            <Row label="Source"><SourceBadge source={order.source} testId="detail-source-badge" /></Row>
            <Row label="Call Center"><span data-testid="detail-cc-name">{order.call_center_name || '—'}</span></Row>
            {canReassign && (
                <div className="pt-3 border-t border-[#E5E7EB] space-y-2" data-testid="reassign-block">
                    <div className="text-xs font-medium text-[#6B7280]">Réassignation manuelle</div>
                    <div className="flex gap-2">
                        <select data-testid="reassign-select" className={inputCls} value={choice} onChange={(e) => setChoice(e.target.value)}>
                            {callCenters.map((cc) => <option key={cc.id} value={cc.id}>{cc.name}</option>)}
                        </select>
                        <button
                            type="button"
                            data-testid="reassign-apply"
                            disabled={!choice || choice === order.assigned_to || assigning}
                            onClick={doAssign}
                            className="h-8 px-3 rounded-md text-xs font-medium bg-gradient-to-r from-[#4B3FD8] to-[#9B7FF4] text-white disabled:opacity-60"
                        >
                            {assigning ? '…' : 'Assigner'}
                        </button>
                    </div>
                </div>
            )}
        </SectionShell>
    );
}

function HistoryTimeline({ history }) {
    if (!history?.length) {
        return (
            <section data-testid="section-history" className="space-y-2">
                <h3 className="text-[13px] font-semibold uppercase tracking-wider text-[#6B7280]">Historique de la commande</h3>
                <div data-testid="history-empty" className="rounded-lg border border-[#E5E7EB] bg-white p-3 text-sm text-[#6B7280]">Aucun changement de statut enregistré.</div>
            </section>
        );
    }
    return (
        <section data-testid="section-history" className="space-y-2">
            <h3 className="text-[13px] font-semibold uppercase tracking-wider text-[#6B7280]">Historique de la commande</h3>
            <ol className="rounded-lg border border-[#E5E7EB] bg-white p-3 space-y-3">
                {history.map((h, i) => {
                    // Phase 16.1 — a non-status "line_items_edited" entry
                    // carries `note` (French summary) instead of a status
                    // transition. Render it with an indigo dot + plain text.
                    if (h.type === 'line_items_edited') {
                        return (
                            <li
                                key={h.id || i}
                                data-testid="history-item"
                                data-history-type="line_items_edited"
                                className="flex gap-3 items-start"
                            >
                                <span className="mt-1.5 inline-block w-2.5 h-2.5 rounded-full shrink-0 bg-[#4B3FD8]" />
                                <div className="min-w-0">
                                    <div data-testid="history-line-items-note" className="text-sm text-[#1A1D2E]">
                                        {h.note || 'Commande éditée.'}
                                    </div>
                                    <div className="text-xs text-[#6B7280] mt-0.5">
                                        {new Date(h.changed_at).toLocaleString('fr-FR')} · {h.changed_by_name}
                                    </div>
                                </div>
                            </li>
                        );
                    }
                    const cfg = ORDER_TRACKING[h.new_status];
                    const dotColor = cfg?.color || '#9CA3AF';
                    return (
                        <li key={h.id || i} data-testid="history-item" className="flex gap-3 items-start">
                            <span className="mt-1.5 inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: dotColor }} />
                            <div className="min-w-0">
                                <div className="text-sm font-medium text-[#1A1D2E]">
                                    <span className="inline-flex items-center h-5 px-2 rounded-md text-[11px] font-medium mr-1" style={badgeStyle(dotColor)}>
                                        {cfg?.label || h.new_status}
                                    </span>
                                    {h.old_status && <span className="text-xs text-[#6B7280]"> depuis {ORDER_TRACKING[h.old_status]?.label || h.old_status}</span>}
                                </div>
                                <div className="text-xs text-[#6B7280] mt-0.5">
                                    {new Date(h.changed_at).toLocaleString('fr-FR')} · {h.changed_by_name}
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ol>
        </section>
    );
}
